import { Host, h } from "@stencil/core";
import monaco, { configureMonacoYaml } from "../../common/monaco/output/monaco.js";
let autoId = 0;
export class ChCodeDiffEditor {
    constructor() {
        this.language = undefined;
        this.modifiedValue = undefined;
        this.options = {
            automaticLayout: true,
            mouseWheelScrollSensitivity: 4,
            mouseWheelZoom: true,
            enableSplitViewResizing: true,
            renderSideBySide: true
        };
        this.readonly = true;
        this.theme = "vs";
        this.value = undefined;
        this.yamlSchemaUri = "";
    }
    #monacoDiffEditorInstance;
    #resizeObserver;
    #editorId = "";
    // Refs
    #monacoRef;
    #absoluteContentRef;
    languageChanged(newLanguage) {
        this.#monacoDiffEditorInstance?.setModel({
            original: monaco.editor.createModel(this.value, newLanguage),
            modified: monaco.editor.createModel(this.modifiedValue, newLanguage)
        });
    }
    modifiedValueChange(newModifiedValue) {
        this.#monacoDiffEditorInstance
            ?.getModel()
            .modified.setValue(newModifiedValue);
    }
    optionsChanged(options) {
        this.#monacoDiffEditorInstance?.updateOptions({
            options
        });
    }
    readonlyChanged(newReadonly) {
        this.#monacoDiffEditorInstance?.updateOptions({
            originalEditable: this.options.originalEditable ?? !newReadonly,
            readOnly: this.options.readOnly ?? newReadonly
        });
    }
    themeChanged(newTheme) {
        this.#monacoDiffEditorInstance?.updateOptions({
            theme: newTheme
        });
    }
    valueChange(newValue) {
        this.#monacoDiffEditorInstance?.getModel().original.setValue(newValue);
    }
    yamlSchemaUriChange(newUri) {
        if (this.language !== "yaml") {
            return;
        }
        // Necessary to not combine the current scheme with the new scheme
        this.#editorId = `ch-diff-editor-${autoId++}`;
        if (newUri) {
            configureMonacoYaml(monaco, {
                enableSchemaRequest: true,
                format: true,
                schemas: this.#getYamlSchemas()
            });
        }
        else {
            configureMonacoYaml(monaco, {
                enableSchemaRequest: true,
                format: true,
                schemas: []
            });
        }
        this.#monacoDiffEditorInstance.setModel({
            original: monaco.editor.createModel(this.value, this.language, monaco.Uri.parse(`file:///${this.#editorId}.txt`)),
            modified: monaco.editor.createModel(this.modifiedValue, this.language, monaco.Uri.parse(`file:///${this.#editorId}-modified.txt`))
        });
    }
    connectedCallback() {
        this.#editorId = `ch-diff-editor-${autoId++}`;
    }
    componentDidLoad() {
        this.#configureYaml();
        this.#setupDiffEditor();
        this.#resizeObserver = new ResizeObserver(entries => {
            const absoluteContentEntry = entries[0].contentRect;
            this.#monacoDiffEditorInstance.layout({
                width: absoluteContentEntry.width,
                height: absoluteContentEntry.height
            });
        });
        this.#resizeObserver.observe(this.#absoluteContentRef);
    }
    disconnectedCallback() {
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = null;
    }
    /**
     * Update the editor's options after the editor has been created.
     * @param options Set of options to be updated
     */
    async updateOptions(options) {
        this.#monacoDiffEditorInstance?.updateOptions(options);
    }
    #getYamlSchemas = () => [
        {
            // If YAML file is opened matching this glob
            fileMatch: [`**/${this.#editorId}.*`],
            // Then this schema will be downloaded from the internet and used.
            uri: this.yamlSchemaUri
        },
        {
            // If YAML file is opened matching this glob
            fileMatch: [`**/${this.#editorId}-modified.*`],
            // Then this schema will be downloaded from the internet and used.
            uri: this.yamlSchemaUri
        }
    ];
    #setupDiffEditor() {
        this.#monacoDiffEditorInstance = monaco.editor.createDiffEditor(this.#monacoRef, {
            ...this.options,
            theme: this.options.theme ?? this.theme,
            originalEditable: this.options.originalEditable ?? !this.readonly,
            readOnly: this.options.readOnly ?? this.readonly
        });
        this.#monacoDiffEditorInstance.setModel({
            original: monaco.editor.createModel(this.value, this.language, monaco.Uri.parse(`file:///${this.#editorId}.txt`)),
            modified: monaco.editor.createModel(this.modifiedValue, this.language, monaco.Uri.parse(`file:///${this.#editorId}-modified.txt`))
        });
    }
    #configureYaml() {
        if (this.language === "yaml" && !!this.yamlSchemaUri) {
            configureMonacoYaml(monaco, {
                enableSchemaRequest: true,
                format: true,
                schemas: this.#getYamlSchemas()
            });
        }
    }
    render() {
        return (h(Host, { key: 'd1e01d57ca561dbd35b4f038bb3f9364a70d7ef7' }, h("div", { key: '48d099e23d11ddc11f6cca3dfebcaf00176a71e6', class: "ch-code-diff-editor-absolute-content", ref: el => (this.#absoluteContentRef = el) }, h("div", { key: 'cb5e5d48992a2a68fb41f2658b2591e3b5e683cb', ref: el => (this.#monacoRef = el) }))));
    }
    static get is() { return "ch-code-diff-editor"; }
    static get originalStyleUrls() {
        return {
            "$": ["code-diff-editor.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["code-diff-editor.css"]
        };
    }
    static get properties() {
        return {
            "language": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the language of the auto created model in the editor."
                },
                "attribute": "language",
                "reflect": false
            },
            "modifiedValue": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the modified value of the diff editor."
                },
                "attribute": "modified-value",
                "reflect": false
            },
            "options": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "CodeDiffEditorOptions",
                    "resolved": "CodeDiffEditorOptions",
                    "references": {
                        "CodeDiffEditorOptions": {
                            "location": "import",
                            "path": "./code-diff-editor-types.js",
                            "id": "src/components/code-diff-editor/code-diff-editor-types.ts::CodeDiffEditorOptions"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the editor options."
                },
                "defaultValue": "{\n    automaticLayout: true,\n    mouseWheelScrollSensitivity: 4,\n    mouseWheelZoom: true,\n    enableSplitViewResizing: true,\n    renderSideBySide: true\n  }"
            },
            "readonly": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the editor should be readonly.\nIf the `readOnly` or `originalEditable` property is specified in the `options` property,\nthis property has no effect."
                },
                "attribute": "readonly",
                "reflect": false,
                "defaultValue": "true"
            },
            "theme": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the theme to be used for rendering."
                },
                "attribute": "theme",
                "reflect": false,
                "defaultValue": "\"vs\""
            },
            "value": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the original value of the diff editor."
                },
                "attribute": "value",
                "reflect": false
            },
            "yamlSchemaUri": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the schema URI for the YAML language."
                },
                "attribute": "yaml-schema-uri",
                "reflect": false,
                "defaultValue": "\"\""
            }
        };
    }
    static get methods() {
        return {
            "updateOptions": {
                "complexType": {
                    "signature": "(options: CodeDiffEditorOptions) => Promise<void>",
                    "parameters": [{
                            "name": "options",
                            "type": "CodeDiffEditorOptions",
                            "docs": "Set of options to be updated"
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "CodeDiffEditorOptions": {
                            "location": "import",
                            "path": "./code-diff-editor-types.js",
                            "id": "src/components/code-diff-editor/code-diff-editor-types.ts::CodeDiffEditorOptions"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Update the editor's options after the editor has been created.",
                    "tags": [{
                            "name": "param",
                            "text": "options Set of options to be updated"
                        }]
                }
            }
        };
    }
    static get watchers() {
        return [{
                "propName": "language",
                "methodName": "languageChanged"
            }, {
                "propName": "modifiedValue",
                "methodName": "modifiedValueChange"
            }, {
                "propName": "options",
                "methodName": "optionsChanged"
            }, {
                "propName": "readonly",
                "methodName": "readonlyChanged"
            }, {
                "propName": "theme",
                "methodName": "themeChanged"
            }, {
                "propName": "value",
                "methodName": "valueChange"
            }, {
                "propName": "yamlSchemaUri",
                "methodName": "yamlSchemaUriChange"
            }];
    }
}
